import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';

/**
 * Pantalla pública /auth/forgot-password (v1.3.1).
 *
 * Se monta FUERA de AuthProvider (ver App.tsx / PublicAuthRouter) → sin sesión,
 * sin TenantInterceptor. El endpoint responde SIEMPRE 200 (no leakea si el email
 * existe), así que tras enviar mostramos un mensaje genérico pase lo que pase.
 * Navegación de vuelta al login vía anchor (recarga completa, sin router).
 */
export function ForgotPasswordScreen() {
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api('/auth/forgot-password', {
        method: 'POST',
        body: { email, tenantSlug },
      });
    } catch {
      // El endpoint responde 200 siempre; ante cualquier fallo de red mostramos
      // igualmente el mensaje genérico (no revelamos nada del estado del email).
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4">
      <div className="card-soft w-full max-w-sm rounded-2xl border border-slate-200/80 bg-white p-8">
        <div className="mb-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
            FluidSol
          </p>
          <h1 className="font-serif text-2xl tracking-tight text-slate-900">
            Restablecer contraseña
          </h1>
        </div>

        {sent ? (
          <div className="space-y-4">
            <p className="rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-600 ring-1 ring-slate-200">
              Si el email existe, recibirás instrucciones para restablecer tu
              contraseña. Revisa tu bandeja (y la carpeta de spam).
            </p>
            <a
              href="/"
              className="block w-full rounded-xl bg-slate-900 px-4 py-2.5 text-center text-sm font-medium text-white transition hover:bg-slate-800"
            >
              Volver al inicio de sesión
            </a>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <p className="text-sm text-slate-500">
              Ingresa tu tenant y email y te enviaremos un enlace para crear una
              nueva contraseña.
            </p>

            <label className="block">
              <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                Identificador del negocio
              </span>
              <input
                className="input"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                autoComplete="organization"
                required
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                Email
              </span>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? 'Enviando…' : 'Enviar instrucciones'}
            </button>

            <a
              href="/"
              className="block text-center text-xs font-medium text-slate-500 underline hover:text-slate-800"
            >
              Volver al inicio de sesión
            </a>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPasswordScreen;
